import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Dropdown, DropdownButton, Container, OverlayTrigger, Tooltip } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-toastify/dist/ReactToastify.css';
import './Products.css';
import { ToastContainer, toast } from 'react-toastify';
import { StoreContext } from '../contexts/StoreContext';
import { ProductsContext } from '../contexts/ProductsContext';
import { convertType }   from '../utils/helper';
import { FaStripeS, FaArchive, FaPencilAlt, FaReddit } from 'react-icons/fa';
import { AuthContext } from '../contexts/AuthContext';
import FullScreenLoader from '../components/FullScreenLoader';
import suntharIcon from '../assets/sunthar.svg';

const STORE_API_URL = process.env.REACT_APP_API_URL;

const SetupCaveMessage = ({ isVisible }) => (
  <div className={`setup-cave-message ${isVisible ? 'visible' : ''}`}>
    <div className="col-md-12 mt-3">
      <div className="card mb-3 p-3" style={{ border: '2px solid #ff5722' }}>
        <div className="card-body">
          <h5 className="card-title" style={{ color: '#ff5722', fontWeight: 'bold' }}>
            You need to set up your cave first
          </h5>
          <p>
            Before you can use this section, you need to set up your <strong>cave</strong>.
            Head over to your Settings page to get started.
          </p>
          <Link to="/settings?addcave=true">
            <button className="btn btn-warning mt-2">
              Add Your First Cave
            </button>
          </Link>
        </div>
      </div>
    </div>
  </div>
);

// Format price by accepting a decimal or a whole number and always format it as a dollar value with 2 decimal places
const formatPrice = (amount) => {
  if (amount || amount === 0) {
    return Number(amount).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
  } else {
    return "";
  }
};

function Products( {defaultType}) {
  const { loading: productsLoading, setLoading, productsArray = [], loadAllProducts, resetProducts, handleArchiveProduct } = useContext(ProductsContext);
  const { stores, selectedStore, setSelectedStore, setStoreLocalStorage, setStoreLogo } = useContext(StoreContext);
  const { role, token } = useContext(AuthContext);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(1);
  const [selectedType, setSelectedType] = useState('');
  const [typeFilterTitle, setTypeFilterTitle] = useState('All');
  const [showActive, setShowActive] = useState(true); 
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  const observer = useRef();

  useEffect(() => {
    const initialize = async () => {
      try {
        setLoading(true);
        if (defaultType === 'showcase') {
          setSelectedType('showcase');
          setTypeFilterTitle('Showcase');
        } else if (defaultType === 'service') {
          setSelectedType('service');
          setTypeFilterTitle('Services');
        } else if (defaultType === 'sale') {
          setSelectedType('sale');
          setTypeFilterTitle('For Sale');
        }
        
        setIsInitialized(true);
      } finally {
        setLoading(false);
      }
    };
    
    initialize();
  }, [defaultType, setLoading]);

  // Add effect to track when stores are loaded
  useEffect(() => {
    if (stores.length >= 0) { // Check if stores array exists (even if empty)
      setIsInitialized(true);
    }
  }, [stores]);

  // Fetch stores only once when the component is mounted or when token changes
  useEffect(() => {
    console.log ('selectedStore: ' + selectedStore);
    if (selectedStore) {
      setLoading(true);
      const fetchInitialProducts = async () => {
        try {
          console.log('fetchInitialProducts store: ' + selectedStore);
          console.log('selectedType: ' + selectedType);
          console.log('showActive: ' + showActive);

          resetProducts();
          const response = await loadAllProducts(selectedStore, selectedType, 1, showActive);
          console.log ('After loadAllProducts');
          console.log ('Response: ' + JSON.stringify(response));

          if (response && response.products) {
            console.log ('Response: ' + JSON.stringify(response));
            setTotalProducts(response.total_products);
            setHasMore(response.products.length < response.total_products);
          }
        } catch (err) {
          setError(err.message);
        }
      };
      fetchInitialProducts();
    }
    setLoading(false);
  }, [selectedStore, selectedType, showActive]);

  const handleActiveFilterChange = (isActive) => {
    setShowActive(isActive);
    resetProducts();
    setCurrentPage(1);
  };

  const handleStoreChange = (e) => {
    const newStoreId = e.target.value;
    setSelectedStore(newStoreId);
    setStoreLocalStorage(newStoreId)

    // Only update store logo if a specific store is selected
    if (newStoreId !== '') {
      const selected = stores.find(store => store._id === newStoreId);
      setStoreLogo(selected ? selected.logo : '');
    } else {
      setStoreLogo(''); // Clear logo when "All Stores" is selected
    }
  };

  const handleTypeChange = (filter, title) => {
    setSelectedType(filter);
    setTypeFilterTitle(title);

    resetProducts();
    setCurrentPage(1);
  };

  const handleArchiveProductClick = async (productId, active) => {
    try {
      await handleArchiveProduct(productId, active);
      toast.success(`Product ${active ? 'resumed' : 'archived'} successfully!`);
    } catch (error) {
      toast.error(error.message || 'An error occurred. Please try again.');
    }
  };

  const handlePushToReddit = async (productId) => {
    try {
      setLoading(true);

      console.log('handlePushToReddit');
      console.log('STORE_API_URL: ' + STORE_API_URL);
      console.log('productId: ' + productId);

      const response = await fetch(`${STORE_API_URL}/products/${productId}/push-to-reddit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include', // ensure cookies/session are sent
      });

      console.log('response: ' + JSON.stringify(response));
      const data = await response.json();
      if (response.ok && data.redditUrl) {
        toast.success('Posted to Reddit!');
      } else {
        toast.error(data.error || 'Failed to post to Reddit.');
      }
    } catch (err) {
      console.error('Error posting to Reddit:', err);
      toast.error('Error posting to Reddit.');
    } finally {
      setLoading(false);
    }
  };

  // Observer callback for infinite scroll
  const lastProductElementRef = useCallback(node => {
    if (loadingMore) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && productsArray.length < totalProducts) {
        setLoadingMore(true);
        setCurrentPage(prevPage => prevPage + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [loadingMore, hasMore, productsArray.length, totalProducts]);

  // Fetch more products when currentPage changes
  useEffect(() => {
    if (currentPage > 1) {
      const fetchMoreData = async () => {
        try {
          const response = await loadAllProducts(selectedStore, selectedType, currentPage, showActive);
          if (response && response.products) {
            console.log('productsArray.length: ' + productsArray.length);
            console.log('response.products.length: ' + response.products.length);
            console.log('response.total_products: ' + response.total_products);

            setHasMore(productsArray.length + response.products.length < response.total_products);
          }
        } catch (err) {
          toast.error(err.message);
        } finally {
          setLoadingMore(false);
        }
      };

      fetchMoreData();
    }
  }, [currentPage]);

  const renderTooltip = (text) => (
    <Tooltip className="d-none d-lg-block">
      {text}
    </Tooltip> // Tooltip visible only on large screens
  );

  return (
    <Container>
      <FullScreenLoader isLoading={productsLoading || !isInitialized} message="Loading products...">
        <ToastContainer />
        <div className="headline d-flex justify-content-between align-items-center">
          <h2 className="underline">{defaultType === 'showcase' ? 'Showcase' : defaultType === 'service' ? 'Services' : 'Products'}</h2>
          <>
            <div className="form-group">
              <label></label>
              <select
                id="storeSelect"
                className="form-select"
                value={selectedStore}
                onChange={handleStoreChange}
              >
                {stores.map((store) => (
                  <option key={store._id} value={store._id}>
                    {store.name}
                  </option>
                ))}
              </select>
            </div>
          </>
        </div>

        {!productsLoading && !error && selectedStore && (
          <>
            <div>
              {defaultType === 'showcase' ? (
                <Link to="/add-showcase">
                  <h6>Add To Showcase</h6>
                </Link>
              ) : defaultType === 'service' ? (
                <Link to="/add-service">
                  <h6>Add Service</h6>
                </Link>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <Link to="/add-product">
                      <h6>Add Product</h6>
                    </Link>
                    <Link to="/waitlist">
                      <h6>Product Waitlist</h6>
                    </Link>
                  </div>
                </>
              )}
            </div>
            <div className="filter-buttons">
              <DropdownButton id="active-filter" title={showActive ? 'Active' : 'Inactive'} align="end" className="me-2">
                <Dropdown.Item onClick={() => handleActiveFilterChange(true)}>Active</Dropdown.Item>
                <Dropdown.Item onClick={() => handleActiveFilterChange(false)}>Archived</Dropdown.Item>
              </DropdownButton>
              {defaultType !== 'showcase' && (
                <DropdownButton id="dropdown-basic-button" title={`${typeFilterTitle}`} align="end">
                  <Dropdown.Item onClick={() => handleTypeChange('', 'All')}>All</Dropdown.Item>
                  <Dropdown.Item onClick={() => handleTypeChange('sale', 'For Sale')}>For Sale</Dropdown.Item>
                  <Dropdown.Item onClick={() => handleTypeChange('service', 'Services')}>Services</Dropdown.Item>
                  <Dropdown.Item onClick={() => handleTypeChange('affiliate', 'Affiliate')}>Affiliate</Dropdown.Item>
                </DropdownButton>
              )}
            </div>
          </>
        )}

        {error && <div style={{ color: 'red' }}>{error}</div>}

        <SetupCaveMessage isVisible={stores.length === 0} />

        {!productsLoading && !error && productsArray.length > 0 && (
          <>
            <h4>Total records: {totalProducts}</h4>
            <div className="row">
              {productsArray.map((product, index) => (
                <div ref={productsArray.length === index + 1 ? lastProductElementRef : null} className="col-lg-3 col-md-4 col-sm-6" key={product._id}>
                  <div className="card mb-4">
                    <div style={{ position: 'relative' }}>
                      <img
                        src={product.images && product.images.length > 0 && product.images[0].cropped_url ? product.images[0].cropped_url : '/placeholder-thumbnail.jpg'}
                        alt={product.name}
                        onClick={() => window.location.href = `/edit-product/${product._id}`}
                        className='card-img-top'
                        style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', cursor: 'pointer' }}
                      />
                      {/* Status label */}
                      <div 
                        className={`status-label ${product.store_published ? 'status-published' : 'status-draft'}`}
                      >
                        {product.store_published ? 'Published' : 'Draft'}
                      </div>
                      {product.published && (
                        <OverlayTrigger placement="top" overlay={renderTooltip('Published on Sunthar.com')}>
                          <img src={suntharIcon} 
                            alt="Sunthar" 
                            style={{ 
                              width: '20px', 
                              height: '20px',
                              position: 'absolute',
                              top: '10px',
                              right: '10px',
                            }} 
                          />
                        </OverlayTrigger>
                      )}
                      {product.stripe_product_id && product.stripe_product_id !== '' &&
                      <FaStripeS
                        aria-label="Sync'd with Stripe"
                        style={{
                          position: 'absolute',
                          bottom: '10px',
                          right: '10px',
                          color: '#FFFFFF',
                          backgroundColor: '#635BFF',
                          fontSize: '20px',
                          padding: '5px',
                          borderRadius: '3px'
                        }}
                      />
                      }
                    </div>
                    <div className="card-body">
                      <div className="product-name">
                        {product.name}
                      </div>
                      <div className="product-description">{product.description}</div>
                      <div className="d-flex flex-row justify-content-between mb-2">
                        <div>{convertType(product.type)}</div>
                        {product.type !== 'showcase' && (
                          <div><strong>{formatPrice(product.price / 100)} {product.currency.toUpperCase()}</strong></div>
                        )}
                      </div>
                      {product.type !== 'showcase' && product.type !== 'affiliate' ? (
                        <>
                          <div className="d-flex flex-row justify-content-between mb-2">
                            <div>Country of Origin: {product.country_of_origin}</div>
                            <div>{product.hs_code && `${product.hs_code}`}</div>
                          </div>
                          <div className="d-flex flex-row justify-content-between mb-2">
                            <div>Inventory: {product.inventory}</div>
                            <div>{product.weight && `${product.weight} lbs`}</div>
                          </div>
                        </>
                      ) : (
                        <div className="d-flex flex-row justify-content-between mb-2">
                          <div># of images: {product.images && product.images.length}</div>
                        </div>
                      )}
                      <div className="d-flex flex-row justify-right mb-0">
                        <div style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                          {product.active ? (
                            <button className="btn btn-danger btn-sm me-2" onClick={() => handleArchiveProductClick(product._id, false)}>
                              <span className="d-flex align-items-center">
                                <FaArchive /> {/* Archive Icon */}
                              </span>
                            </button>
                          ) : (
                            <button className="btn btn-secondary btn-sm me-2" onClick={() => handleArchiveProductClick(product._id, true)}>
                              <span className="d-flex align-items-center">
                                <FaArchive /> {/* Resume Icon */}
                              </span>
                            </button>
                          )}
                          {role === 'super' && product.type === 'showcase' && (
                            <button
                              className="btn btn-warning btn-sm me-2"
                              onClick={() => handlePushToReddit(product._id)}
                              title="Push to Reddit"
                            >
                              <span className="d-flex align-items-center">
                                <FaReddit />
                              </span>
                            </button>
                          )}
                          <button className="btn btn-primary btn-sm" onClick={() => window.location.href = `/edit-product/${product._id}`}>
                            <span className="d-flex align-items-center">
                              <FaPencilAlt /> {/* Edit Icon */}
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        {loadingMore && <div>Loading more products...</div>}
      </FullScreenLoader>
    </Container>
  );
}

export default Products;
